import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import {
  IApiResponse,
  IDisposition,
  IDispositionListFilter,
  IDispositionListResponse,
  IDispositionNameResponse,
  IDispositionListSort,
  IListDispositionsRequestBody,
  ICreateDispositionRequest,
  ICreateDispositionResponse
} from '../../state/models/disposition.model';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { HttpClient, HttpParams } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class DispositionService {
  private readonly apiUrl = `${environment.dispositionEndpoint}/api/disposition`;

  constructor(
    private readonly http: HttpClient,
    private readonly apiService: ApiService
  ) {}

  getDispositions(
    page: number = 1,
    limit: number = 10,
    filter?: IDispositionListFilter,
    sort?: IDispositionListSort
  ): Promise<IDispositionListResponse> {
    return this.apiService.post<IDispositionListResponse>(
      `${this.apiUrl}/list?page=${page}&limit=${limit}`,
      { filter, sort }
    );
  }

  searchDispositions(
    name?: string,
    status?: 'Active' | 'Inactive'
  ): Observable<IDisposition[]> {
    const params: any = { filter: {} };
    if (name) params.filter.disposition_name = name;
    if (status) params.filter.is_active = status;
    return from(
      this.apiService.post<IDisposition[]>(`${this.apiUrl}/list`, { params })
    );
  }

  getDisposition(id: number): Observable<IApiResponse<IDisposition>> {
    return from(
      this.apiService.get<IApiResponse<IDisposition>>(`${this.apiUrl}/${id}`)
    );
  }
  createDisposition(
    disposition: ICreateDispositionRequest
  ): Observable<IApiResponse<ICreateDispositionResponse | IDisposition>> {
    return from(
      this.apiService.post<
        IApiResponse<ICreateDispositionResponse | IDisposition>
      >(`${this.apiUrl}/`, disposition)
    );
  }

  updateDisposition(
    id: number,
    disposition: Partial<IDisposition>
  ): Observable<IApiResponse<IDisposition>> {
    return from(
      this.apiService.put<IApiResponse<IDisposition>>(
        `${this.apiUrl}/${id}`,
        disposition
      )
    );
  }

  deleteDisposition(id: number): Observable<IApiResponse<IDisposition>> {
    return from(
      this.apiService.delete<IApiResponse<IDisposition>>(`${this.apiUrl}/${id}`)
    );
  }

  bulkDeleteDispositions(
    ids: (number | undefined)[]
  ): Observable<IApiResponse<IDisposition>> {
    return from(
      this.apiService.deleteWithBody<IApiResponse<IDisposition>>(
        `${this.apiUrl}/bulk-delete`,
        { ids }
      )
    );
  }
  toggleStatus(id: number): Observable<IApiResponse<IDisposition>> {
    return from(
      this.apiService.patch<IApiResponse<IDisposition>>(
        `${this.apiUrl}/${id}`,
        {}
      )
    );
  }

  listDispositionName(): Observable<IApiResponse<IDispositionNameResponse>> {
    return from(
      this.apiService.get<IApiResponse<IDispositionNameResponse>>(
        `${this.apiUrl}/get-dispositions-list/active`,
        {}
      )
    );
  }

  listAllStatusDispositionNames(): Observable<
    IApiResponse<IDispositionNameResponse>
  > {
    return from(
      this.apiService.get<IApiResponse<IDispositionNameResponse>>(
        `${this.apiUrl}/get-dispositions-list/all`,
        {}
      )
    );
  }

  exportDispositions(
    body: IListDispositionsRequestBody,
    page = 1,
    limit = 10
  ): Promise<Blob> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.apiService.postBlob<Blob>(
      `${this.apiUrl}/export`,
      body,
      params,
      undefined,
      { responseType: 'blob' as 'json' }
    );
  }
}
